import { useEffect, useState } from "react";
import { catalogThumbMiss, loadCatalogThumb, peekCatalogThumb, rememberCatalogThumbMiss } from "../lib/catalogThumbCache";
import { ui } from "../ui";

interface Props {
  /** Engine artifact URL, or a same-origin demo asset. Null or a failed load shows the file-type mark. */
  src?: string | null;
  alt?: string;
  className?: string;
  /** File-type label when there is no image, e.g. STEP, DXF, PART. */
  mark?: string;
}

function isDirectImageSrc(src: string): boolean {
  return src.startsWith("blob:") || src.startsWith("data:");
}

function DrawingMark() {
  return (
    <svg viewBox="0 0 32 32" width="20" height="20" aria-hidden="true" className="size-5 shrink-0">
      <rect x="5" y="7" width="22" height="18" rx="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M9 21 14 12l4 5 2.5-2.5L24 21" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}

function ThumbPlaceholder({ mark, alt }: { mark: string; alt: string }) {
  const label = alt || mark;
  return (
    <span
      role="img"
      aria-label={label || undefined}
      data-testid="thumb-fallback"
      data-ext={mark || undefined}
      className="flex size-full flex-col items-center justify-center gap-0.5 overflow-hidden text-primary"
    >
      <DrawingMark />
      {mark ? (
        <span className="max-w-full truncate font-mono text-[9px] leading-none font-medium tracking-wide">
          {mark}
        </span>
      ) : null}
    </span>
  );
}

/**
 * Part preview with a file-type fallback.
 *
 * Artifact URLs are fetched with the session (cookie + Bearer). A plain
 * `<img src>` cannot send the Bearer and, when the API is on another origin,
 * never hits the engine. Failures stay cached for the session so a 404 is
 * not requested again on each list render.
 */
export function Thumb({ src, alt = "", className = ui.fileThumb, mark = "" }: Props) {
  const [objectUrl, setObjectUrl] = useState<string | null>(() => {
    if (!src || isDirectImageSrc(src)) return src || null;
    return peekCatalogThumb(src);
  });
  const [failed, setFailed] = useState(() => Boolean(src && !isDirectImageSrc(src) && catalogThumbMiss(src)));

  useEffect(() => {
    if (!src) {
      setObjectUrl(null);
      setFailed(false);
      return;
    }
    if (isDirectImageSrc(src)) {
      setObjectUrl(src);
      setFailed(false);
      return;
    }
    if (catalogThumbMiss(src)) {
      setObjectUrl(null);
      setFailed(true);
      return;
    }
    const cached = peekCatalogThumb(src);
    if (cached) {
      setObjectUrl(cached);
      setFailed(false);
      return;
    }
    let cancel = false;
    void loadCatalogThumb(src)
      .then((url) => {
        if (cancel) return;
        setObjectUrl(url);
        setFailed(false);
      })
      .catch(() => {
        if (cancel) return;
        setObjectUrl(null);
        setFailed(true);
      });
    return () => {
      cancel = true;
    };
  }, [src]);

  const showImage = Boolean(objectUrl) && !failed;

  return (
    <span className={className}>
      <span>
        {showImage ? (
          <img
            src={objectUrl || undefined}
            alt={alt}
            data-thumb="true"
            data-testid="thumb-image"
            onError={() => {
              if (src) rememberCatalogThumbMiss(src);
              setFailed(true);
              setObjectUrl(null);
            }}
          />
        ) : (
          <ThumbPlaceholder mark={mark} alt={alt} />
        )}
      </span>
    </span>
  );
}
